import type {
  SummarySegment,
  TeamLink,
  TeamMapNode,
} from '../types/effectiveness';

/**
 * Demo content for the effectiveness view. The story: eight teams under one IT
 * department head, ranked by how cleanly each one's work crosses to the next
 * team. The unit is the WORK INTERFACE between two teams - and the conflict is
 * the everyday kind a manager keeps having to raise: handoffs that land late,
 * work that comes back, and tickets that get reopened and bounce back and forth.
 * Two interfaces generate more of that friction than every other handoff
 * combined; the rest range from quiet rework to clean. Numbers are illustrative
 * and chosen to read as one coherent snapshot of an IT org under real load.
 */

/** The single verdict sentence at the top of the page. */
export const EFFECTIVENESS_VERDICT: SummarySegment[] = [
  {
    text: 'Two interfaces in your IT org throw the same conflict every week - access requests bounce back and forth between ',
  },
  { text: 'Security Operations', highlight: 'bad' },
  { text: ' and ' },
  { text: 'Identity & Access', highlight: 'bad' },
  { text: ', and ' },
  { text: 'Infrastructure & Cloud', highlight: 'bad' },
  { text: ' keeps handing late, half-finished work to the Service Desk.' },
];

/** One-line read under the verdict - the scope and where the cost sits. */
export const EFFECTIVENESS_SUBLINE =
  '8 teams across your IT department, ranked by how cleanly their work crosses to the next team. The two red interfaces below are where deliveries land late, come back reopened, and eat more of your week than every other handoff combined.';

/**
 * The team-to-team links, authored worst-first. Each is one work interface: the
 * team producing the work, the team it lands on, the recurring friction that
 * shows up there, and the cost.
 */
export const TEAM_LINKS: TeamLink[] = [
  {
    id: 'sec-iam',
    from: 'Security Operations',
    to: 'Identity & Access',
    severity: 'critical',
    eyebrow: 'Most reopened',
    caseId: 'EX-908',
    reason:
      'Access and role-change requests bounce between the two teams - Security Operations files them, Identity & Access kicks them back for missing detail, and each one is reopened three or four times before anyone actions it.',
    detail:
      'There is no shared definition of a complete request, so every handoff opens another round of clarification. The same tickets reopen week after week, and you hear "still blocked on IAM" and "SecOps never sent what we need" in the same standup.',
    signals: [
      'Access requests reopened 3.4 times on average before they close',
      '11 requests bounced back and forth for over a week last sprint',
      'No agreed definition of ready for an access request',
    ],
    incidentRef: 'REQ-2208',
    example:
      'One joiner-access request was reopened five times across nine days - each pass bounced back for a field the other team assumed they would fill.',
    secondaryStats: [
      { value: '3.4x', label: 'reopened per request' },
      { value: '11', label: 'stuck over a week' },
      { value: '0', label: 'agreed handoff spec' },
    ],
    recommendation:
      'Agree a definition-of-ready checklist so a request crosses only once it is complete, and track reopens per request as the health number for this interface.',
    metricValue: '3.4x',
    metricLabel: 'reopened before it lands',
  },
  {
    id: 'infra-servicedesk',
    from: 'Infrastructure & Cloud',
    to: 'Service Desk',
    severity: 'critical',
    caseId: 'EX-915',
    reason:
      'Infrastructure hands changes and new environments to the Service Desk late and without notice, so the same scramble repeats every release - the desk is caught flat, the queue floods, and users wait.',
    detail:
      'Planned work keeps arriving as a surprise. Infrastructure schedules its own windows and the Service Desk finds out through the ticket spike, so a known change gets worked as an unknown outage - the same way, every time.',
    signals: [
      'Change handoffs reach the Service Desk a median 2 days late',
      'Repeat ticket flood on 6 of the last 8 change windows',
      'No standing pre-change handoff between the two teams',
    ],
    incidentRef: 'CHG-5573',
    example:
      'The last three maintenance windows each landed on the Service Desk with no heads-up - the same "why is this down" flood, three months running.',
    secondaryStats: [
      { value: '2d', label: 'late per handoff' },
      { value: '6 / 8', label: 'changes flooded the desk' },
      { value: '0', label: 'advance handoffs' },
    ],
    recommendation:
      'Make a Service Desk handoff a required gate on every change - no window opens until the notice and a holding statement are in their hands.',
    metricValue: '2d',
    metricLabel: 'late on every change handoff',
  },
  {
    id: 'endpoint-servicedesk',
    from: 'Endpoint Management',
    to: 'Service Desk',
    severity: 'elevated',
    caseId: 'EX-870',
    reason:
      'Endpoint rollouts go out half-tested, so the same builds keep coming back broken - the Service Desk reopens the tickets and the fix bounces back to the one engineer who pushed it.',
    detail:
      'Patches ship fleet-wide in a single ring with no canary and no rollback handed over, so a bad build lands on everyone at once and the desk has nothing to do but reopen and wait. It is the same loop most rings.',
    signals: [
      '1 in 4 endpoint changes reopened after a failed rollout',
      'Average 2 reopen cycles before a bad build is settled',
      'No tested rollback handed over with the change',
    ],
    incidentRef: 'EUC-1841',
    example:
      'The last two patch rings each came back within a day - the Service Desk reopened both and waited on the same engineer to reverse them.',
    secondaryStats: [
      { value: '24%', label: 'rollouts reopened' },
      { value: '2x', label: 'reopen cycles per bad build' },
      { value: '0', label: 'rollbacks handed over' },
    ],
    recommendation:
      'Ship in rings - a canary group first, fleet-wide only once it holds - and hand the Service Desk a one-click backout so a bad build never bounces to a single person.',
    metricValue: '24%',
    metricLabel: 'rollouts come back reopened',
  },
  {
    id: 'network-sec',
    from: 'Network Operations',
    to: 'Security Operations',
    severity: 'elevated',
    caseId: 'EX-844',
    reason:
      'Network changes go in without a record, so Security Operations reopens the same investigations over and over - every week starts by ruling out an un-logged change before any real work begins.',
    detail:
      'The two teams share the perimeter but not a change log. When an alert fires, SecOps cannot tell a real problem from an un-recorded Network change, so the back-and-forth to find out repeats most weeks.',
    signals: [
      'Roughly 19 un-logged changes a month land back on SecOps as questions',
      'About a day a week lost reconstructing what Network changed',
      'No shared change record between the two teams',
    ],
    incidentRef: 'NET-0992',
    example:
      'Three times last month an alert turned out to be an un-logged Network change - each one a day of back-and-forth before it could be closed.',
    secondaryStats: [
      { value: '19', label: 'un-logged changes / mo' },
      { value: '~1d', label: 'a week to back-and-forth' },
      { value: '0', label: 'shared change records' },
    ],
    recommendation:
      'Route every firewall and ACL change through a ticket that posts to the SecOps channel, so an alert can be matched to a known change in seconds instead of a day.',
    metricValue: '~1d',
    metricLabel: 'a week lost to back-and-forth',
  },
  {
    id: 'appsupport-data',
    from: 'Application Support',
    to: 'Data & BI',
    severity: 'watch',
    caseId: 'EX-790',
    reason:
      'App teams keep changing schemas without telling BI, so dashboards quietly break and BI reopens the same "the numbers look wrong" thread every few weeks.',
    detail:
      'There is no schema-change contract between the apps and the warehouse. A column gets renamed, the ETL keeps running, the dashboard drifts to zero - and it is found by a confused exec, not a test, again and again.',
    signals: [
      '5 breaking schema changes shipped unannounced last quarter',
      'A broken dashboard goes a median 4 days before anyone notices',
      'No schema-change heads-up reaches the BI team',
    ],
    incidentRef: 'BI-3310',
    example:
      'A "status" column rename broke the exec availability board for the second time this quarter - it read 0% for four days before a VP asked why.',
    secondaryStats: [
      { value: '5', label: 'breaking changes / quarter' },
      { value: '4d', label: 'until anyone notices' },
      { value: '0', label: 'schema heads-ups' },
    ],
    recommendation:
      'Add a schema-change check to the app CI that pings BI before a column on a published table is renamed or dropped.',
    metricValue: '4d',
    metricLabel: 'dashboards stale before it is caught',
  },
  {
    id: 'iam-servicedesk',
    from: 'Identity & Access',
    to: 'Service Desk',
    severity: 'healthy',
    caseId: 'OK',
    reason:
      'Password and MFA requests come through complete and mostly self-served, so almost nothing bounces back - this interface barely generates a ticket.',
    detail:
      'The contract here is clean end to end: users self-serve, requests arrive ready, and the Service Desk only sees the genuine edge cases. Worth protecting as the noisier interfaces get fixed.',
    signals: [
      '92% of password resets last month were self-served',
      'Under 1% of requests reopened for missing detail',
      'Median lockout cleared in under 3 minutes',
    ],
    example:
      '92% of the resets last month never reached a human - the Service Desk only saw the 8% genuine edge cases, and almost none came back.',
    secondaryStats: [
      { value: '92%', label: 'resets self-served' },
      { value: '<1%', label: 'requests reopened' },
      { value: '0', label: 'escalations past tier 1' },
    ],
    recommendation:
      'No action needed - this is the template for what a clean interface looks like against the links above.',
    metricValue: 'Nominal',
    metricLabel: '',
  },
  {
    id: 'data-servicedesk',
    from: 'Data & BI',
    to: 'Service Desk',
    severity: 'healthy',
    caseId: 'OK',
    reason:
      'Service Desk SLA and backlog boards refresh straight from BI, so the teams steer off the same live numbers instead of arguing over a hand-built export.',
    detail:
      'One source of truth feeds the daily standup: the board refreshes itself from the ticketing system, so no two teams quote different backlog numbers and nothing bounces back for a recount.',
    signals: [
      'SLA and backlog board refreshes every 15 minutes from source',
      'Zero manual status exports in the last quarter',
      'One source of truth - no two teams quoting different numbers',
    ],
    example:
      'The daily standup runs off a live BI board - the last hand-built "ticket aging" export was retired four months ago and never missed.',
    secondaryStats: [
      { value: '15 min', label: 'board refresh' },
      { value: '0', label: 'manual exports / quarter' },
      { value: '1', label: 'source of truth' },
    ],
    recommendation:
      'No action needed - keep the board as the single source as ticket volume grows.',
    metricValue: 'Nominal',
    metricLabel: '',
  },
];

/**
 * Where each team sits on the dependency map. The Service Desk anchors the
 * center as the team most work escalates to and changes land on; the rest ring
 * around it. Names match the from/to fields in TEAM_LINKS so the edges resolve
 * against these nodes.
 */
export const TEAM_MAP_NODES: TeamMapNode[] = [
  { name: 'Service Desk', x: 50, y: 50 },
  { name: 'Infrastructure & Cloud', x: 22, y: 22 },
  { name: 'Security Operations', x: 76, y: 20 },
  { name: 'Identity & Access', x: 86, y: 44 },
  { name: 'Network Operations', x: 80, y: 78 },
  { name: 'Endpoint Management', x: 50, y: 88 },
  { name: 'Application Support', x: 20, y: 76 },
  { name: 'Data & BI', x: 14, y: 46 },
];
